import * as React from "react";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DaoProvider } from "fe-web-components/diseaseview/DaoContext";
import { WidgetSettingsContext } from "../widget/useWidgetSettings";
import { IntlProvider } from "react-intl";
import { ClinicDao, WebDao } from "fe-common/graphql/dao";
import { gqlClient } from "fe-common/graphql/webClient";
import { getVersion } from "../VersionInfo";
import { ClinicDaoProvider } from "fe-web-components/clinic/ClinicContext";
import { KahunaWidgetConfiguration } from "../d";
import {
  ClinicContextData,
  ClinicDaoData,
  ClinicDataContextProvider,
} from "fe-web-components/clinicDataProvider/clinicProvider";
import { useQueryParams } from "fe-common/featureFlags/getFeatureFlags";
import { ConversationDriverProvider } from "../driver/ConversationDriverProvider";
import { setAnalyticsSession } from "fe-common/analytics/useSetAnalyticsSession";
import { MergedConfigurationSettings } from "@kahunmedical/common-configuration/dist/lib/settings";
import { ClinicGeneralOptions } from "@kahunmedical/common-configuration/dist/configuration/clinic";
import {
  LocaleContextProvider,
  useLocaleContext,
} from "fe-common/locale/localeContext";
import { getClinicDataFromServer } from "fe-web-components/clinicDataProvider/getClinicDataFromServer";
import { readV2SettingsFromLocalStorage } from "fe-web-components/settings/common/readV2SettingsFromLocalStorage";
import { ConfigEnvironmentSettingsAccess } from "@kahunmedical/common-configuration";
import { getV2ClinicSettings } from "fe-web-components/settings/common/getV2ClinicSettings";
import {
  ConfigSettingsEnvironments,
  ConfigSettingsKeyPerEnvironment,
} from "fe-web-components/settings/common/ConfigSettingsKeyPerNodeEnvironment";
import { TopLevelLoadingContainer } from "fe-web-components/loader/loadingContainer";
import { DefaultClinicIds } from "fe-web-components/clinicDataProvider/defaulyClinicIds";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";
import Helmet from "react-helmet";

//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export interface CommonStateWrapperProps {
  locale?: string;
  children?: React.ReactNode;
  widgetSettings?: KahunaWidgetConfiguration;
  embedded?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
type InitAlertType = {
  text?: string;
  textId?: string;
};
//endregion [[ Functions ]]
/***
 * Topmost level common state used in by app variants in, Do, partner,
 * translations and conversations
 * @param props
 * @constructor
 */

export const CommonStateWrapper = ({ ...props }: CommonStateWrapperProps) => {
  const defaultLocale = props.locale || props.widgetSettings?.locale;
  const baseLocale = defaultLocale?.split("_")[0]; //remove country suffix from locale <- should be canceled if using custom translations for a country

  return (
    <LocaleContextProvider
      baseLocale={baseLocale}
      partnerId={props.widgetSettings?.partnerId}
    >
      <CommonStateWrapperInner {...props} />
    </LocaleContextProvider>
  );
};

export const initAlertContext = createContext<InitAlertType>(null);
const InitAlertProvider = initAlertContext.Provider;

export const CommonStateWrapperInner = ({
  ...props
}: CommonStateWrapperProps) => {
  const queryParams = useQueryParams();
  const patientCaseId =
    queryParams.get("patientCaseId") || props.widgetSettings?.patientCaseId;
  const externalLocale =
    queryParams.get("lang") || props.locale || props.widgetSettings?.locale;

  const [loading, setLoading] = useState(true);
  const [patientCaseMissing, setPatientCaseMissing] = useState<boolean>(false);
  const [clinicContextValue, setClinicContextValue] = useState<
    ClinicContextData
  >(null);

  const {
    locale,
    changeLocale,
    translations,
    setClinicConfig: setLocaleClinicConfig,
    setConversationOverrides,
  } = useLocaleContext();

  const headers = useMemo(() => ({ "kahun-langcode": locale }), [locale]);
  const widgetIgnoreCache = props?.widgetSettings?.ignoreCache;
  if (widgetIgnoreCache) {
    headers["Cache-Control"] = "no-cache";
  }

  const WEB_DAO = useRef(new WebDao(() => gqlClient(headers)));
  const CLINIC_DAO = useRef(new ClinicDao(() => gqlClient(headers)));

  const clinicId =
    props.widgetSettings?.clinicId ||
    DefaultClinicIds[process.env.REACT_APP_ENVIRONMENT];

  const mergeClinicDataWithEnv = useCallback(
    async (clinicData: ClinicDaoData) => {
      const envStr =
        ConfigSettingsKeyPerEnvironment[process.env.REACT_APP_ENVIRONMENT] ||
        ConfigSettingsEnvironments.Development;

      const lsData = readV2SettingsFromLocalStorage();
      const globalSettings = await new ConfigEnvironmentSettingsAccess().getSettings(
        envStr,
      );
      // console.log("CLINIC INIT - GLOBAL", globalSettings);
      // console.log("CLINIC INIT - MERGED", clinicData?.mergedConfiguration);
      // console.log("CLINIC INIT - FLAT CONFIG", clinicData?.config);
      return new MergedConfigurationSettings(
        {},
        lsData,
        clinicData?.mergedConfiguration || [
          clinicData?.name,
          getV2ClinicSettings(clinicData?.config),
        ],
        globalSettings,
      );
    },
    [],
  );

  const validatePatientCase = useCallback(
    async (patientCaseId: string, clinicId: string) => {
      if (patientCaseId) {
        const pc = await WEB_DAO.current.restorePatientCase(
          patientCaseId,
          clinicId,
        );
        const { data, findings } = pc;

        const patientCaseData = data ? JSON.parse(data.payload) : null;
        if (!Object.values(findings).length) {
          setPatientCaseMissing(true);
        }
        return patientCaseData;
      }
    },
    [],
  );

  const partnerId = props?.widgetSettings?.partnerId;

  //App init: fetch clinic settings --> init locale --> init webDao --> set context --> set analytics --> done loading
  useEffect(() => {
    (async () => {
      //create merged settings
      const clinicData = await getClinicDataFromServer(
        CLINIC_DAO.current,
        clinicId,
      );
      if (!clinicData) {
        throw new Error("Invalid clinic id" + clinicId);
      }
      const mergedSettings = await mergeClinicDataWithEnv(clinicData);
      const clinicConfig = mergedSettings?.makeConfigObjectForKey(
        ClinicGeneralOptions,
        "clinic",
      );

      //set locale
      const newLocale = externalLocale || clinicConfig?.defaultLocale;
      setLocaleClinicConfig(clinicData?.config);
      changeLocale(newLocale);

      //init webDao with the settings
      WEB_DAO.current.updateSettingsObject(mergedSettings);

      //check if patient case exists
      const patientCaseData = await validatePatientCase(
        patientCaseId,
        clinicId,
      );
      setConversationOverrides(patientCaseData?.conversationOverrides); //control text per request.

      // set clinic context to be used in the app
      const clinicGeneralSettings = mergedSettings?.makeConfigObjectForKey?.(
        ClinicGeneralOptions,
        "clinic",
      );

      setClinicContextValue({
        isClinicMode: clinicGeneralSettings?.clinicMode === true,
        ...clinicData,
        settings: mergedSettings,
        clinicSettings: clinicGeneralSettings,
      });

      const userName = await getCurrentUserEmail();
      //set analytics
      setAnalyticsSession({
        privateMode: clinicConfig?.privateMode === true,
        partnerId,
        version: getVersion(),
        name: userName,
      });

      //done
      setLoading(false);
    })();
  }, [
    validatePatientCase,
    partnerId,
    changeLocale,
    externalLocale,
    setConversationOverrides,
    setLocaleClinicConfig,
    mergeClinicDataWithEnv,
    patientCaseId,
    clinicId,
  ]);

  const googleTrackingTag = useMemo(() => {
    const enabled = clinicContextValue?.settings.getValueForKey(
      "clinic.conversionTracking.googleTagEnabled",
    )?.value;
    return enabled
      ? clinicContextValue.settings.getValueForKey(
          "clinic.conversionTracking.customGoogleTag",
        )?.value
      : null;
  }, [clinicContextValue]);

  const facebookTrackingPixel = useMemo(() => {
    const enabled = clinicContextValue?.settings.getValueForKey(
      "clinic.conversionTracking.metaPixelEnabled",
    )?.value;
    return enabled
      ? clinicContextValue.settings.getValueForKey(
          "clinic.conversionTracking.customMetaPixel",
        )?.value
      : null;
  }, [clinicContextValue]);

  // if locale was updated make sure to update the headers
  useEffect(() => {
    WEB_DAO.current?.updateClient(() =>
      gqlClient(headers, true, undefined, true),
    );
  }, [headers]);

  if (loading || !Object.keys(translations).length)
    return props.embedded ? null : <TopLevelLoadingContainer />;

  return (
    <>
      {(googleTrackingTag || facebookTrackingPixel) && (
        <Helmet>
          {googleTrackingTag && <script>{`${googleTrackingTag}`}</script>}
          {facebookTrackingPixel && (
            <script>{`${facebookTrackingPixel}`}</script>
          )}
        </Helmet>
      )}
      <DaoProvider value={WEB_DAO.current}>
        <WidgetSettingsContext widgetSettings={props.widgetSettings}>
          <ClinicDaoProvider value={CLINIC_DAO.current}>
            <ClinicDataContextProvider value={clinicContextValue}>
              <IntlProvider
                messages={translations}
                locale={locale.split("_").pop()}
                defaultLocale={locale.split("_").pop()}
                onError={(err) => {
                  if (err.code === "MISSING_TRANSLATION") {
                    console.warn("Missing translation", err.message);
                    return;
                  }
                }}
                defaultRichTextElements={{
                  b: (chunks) => <b>{chunks}</b>,
                  br: () => <br />,
                  p: (chunks) => <p>{chunks}</p>,
                  span: (chunks) => <span>{chunks}</span>,
                }}
              >
                <ConversationDriverProvider>
                  <InitAlertProvider
                    value={
                      patientCaseMissing
                        ? { textId: "CONVERSATION_NOT_FOUND" }
                        : null
                    }
                  >
                    {props.children}
                  </InitAlertProvider>
                </ConversationDriverProvider>
              </IntlProvider>
            </ClinicDataContextProvider>
          </ClinicDaoProvider>
        </WidgetSettingsContext>
      </DaoProvider>
    </>
  );
};
